/**
 * 数字资产 · 角色 / 场景 资产列表面板
 * - 顶部:搜索框 + 「新建」按钮
 * - 网格:AssetCard,空态文案
 * - 点击卡片 → AssetDetailDrawer
 * - 点击新建 → CreateAssetDialog
 */
import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { AssetCard } from './AssetCard';
import { CreateAssetDialog } from './CreateAssetDialog';
import { AssetDetailDrawer } from './AssetDetailDrawer';
import type { BrainAsset, BrainAssetListResponse, BrainKind } from './types';

interface Props {
  kind: Extract<BrainKind, 'character' | 'location' | 'prop'>;
}

const KIND_LABEL: Record<Props['kind'], string> = {
  character: '角色',
  location: '场景',
  prop: '道具',
};

async function fetchAssets(kind: Props['kind']): Promise<BrainAssetListResponse> {
  return await api
    .get('brain/assets', { searchParams: { kind, limit: 200 } })
    .json<BrainAssetListResponse>();
}

export function AssetListPanel({ kind }: Props) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<BrainAsset | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BrainAsset | null>(null);
  const label = KIND_LABEL[kind];

  const { data, isLoading } = useQuery({
    queryKey: ['brain', 'assets', kind],
    queryFn: () => fetchAssets(kind),
    placeholderData: (prev) => prev,
  });

  const deleteMut = useMutation({
    mutationFn: async (assetId: string) => {
      await api.delete(`brain/assets/${assetId}`).json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['brain', 'assets', kind] });
      toast.success('已删除');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '网络异常'),
  });

  const reorderMut = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await api
        .post('brain/assets/reorder', { json: { kind, orderedIds } })
        .json();
    },
    onError: (e: Error) => {
      toast.error('排序保存失败', e.message || '请刷新重试');
      qc.invalidateQueries({ queryKey: ['brain', 'assets', kind] });
    },
  });

  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    const key = q.trim().toLowerCase();
    if (!key) return items;
    return items.filter(
      (a) =>
        a.name.toLowerCase().includes(key) ||
        JSON.stringify(a.attributes ?? {}).toLowerCase().includes(key),
    );
  }, [data, q]);

  const handleDelete = (a: BrainAsset) => {
    setDeleteTarget(a);
  };

  const handleDrop = (targetId: string) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const current = data?.items ?? [];
    const fromIdx = current.findIndex((x) => x.assetId === dragId);
    const toIdx = current.findIndex((x) => x.assetId === targetId);
    if (fromIdx === -1 || toIdx === -1) {
      setDragId(null);
      setOverId(null);
      return;
    }
    const next = [...current];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // 重新分配 sortOrder 用于本地排序;后端会回写权威值
    const reordered = next.map((x, i) => ({ ...x, sortOrder: i + 1 }));
    qc.setQueryData<BrainAssetListResponse>(['brain', 'assets', kind], (prev) =>
      prev ? { ...prev, items: reordered } : prev,
    );
    setDragId(null);
    setOverId(null);
    reorderMut.mutate(reordered.map((x) => x.assetId));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-outline" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`搜索${label}名称或维度…`}
            className="pl-9"
          />
        </div>
        <span className="text-xs text-neutral-500">
          {isLoading ? '加载中…' : `共 ${filtered.length} 个${label}`}
        </span>
        <div className="ml-auto">
          <Button variant="agent" agentColor="brain" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            新建{label}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <section className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-lg bg-surface-variant" />
          ))}
        </section>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-outline-variant p-12 text-center text-sm text-neutral-500">
          {q
            ? '没有匹配的资产,试试其它关键词。'
            : `还没有任何${label},点击右上「+ 新建${label}」开始创建,通过多视图设定图锁定核心形象。`}
        </div>
      ) : (
        <section className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filtered.map((a) => (
            <div
              key={a.assetId}
              draggable={!q.trim()}
              onDragStart={(e) => {
                setDragId(a.assetId);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragEnd={() => {
                setDragId(null);
                setOverId(null);
              }}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (overId !== a.assetId) setOverId(a.assetId);
              }}
              onDragLeave={() => {
                if (overId === a.assetId) setOverId(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(a.assetId);
              }}
              className={cn(
                'h-full transition',
                dragId === a.assetId && 'opacity-40',
                overId === a.assetId && dragId && dragId !== a.assetId && 'ring-2 ring-brand-400 rounded-lg',
              )}
            >
              <AssetCard
                asset={a}
                onOpen={setDetail}
                onDelete={handleDelete}
              />
            </div>
          ))}
        </section>
      )}

      <CreateAssetDialog
        open={createOpen}
        kind={kind}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['brain', 'assets', kind] });
        }}
      />

      <AssetDetailDrawer
        open={!!detail}
        asset={detail}
        onClose={() => setDetail(null)}
        onChanged={() => {
          qc.invalidateQueries({ queryKey: ['brain', 'assets', kind] });
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title={deleteTarget ? `删除${label}「${deleteTarget.name}」?` : `删除${label}?`}
        description="该资产的所有生成记录与锁定图也会一并删除,操作不可恢复。"
        confirmText="删除"
        tone="danger"
        pending={deleteMut.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          const id = deleteTarget.assetId;
          setDeleteTarget(null);
          deleteMut.mutate(id);
        }}
      />
    </div>
  );
}
